import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { RecordingPlayer } from '../components/RecordingPlayer';
import { useSubmissions } from '../hooks/useSubmissions';
import { LAYER_LABELS } from '../types/capsule';
import { parseCourseSubmissionSlug, COURSE_LAYER_LABELS } from '../types/course';
import type { SubmissionIndexEntry, Submission, Feedback } from '../types/submission';
import type { LayerName } from '../types/capsule';
import type { CourseLayerName } from '../types/course';
import '../styles/SubmissionHistoryPage.css';

// TODO: move to shared utils
function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// TODO: move to shared utils
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function SubmissionCard({ entry }: { entry: SubmissionIndexEntry }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<Submission | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const courseInfo = parseCourseSubmissionSlug(entry.capsuleSlug);
  const isCourse = courseInfo !== null;

  const layerLabel = isCourse
    ? (COURSE_LAYER_LABELS[entry.layerName as CourseLayerName] || entry.layerName)
    : (LAYER_LABELS[entry.layerName as LayerName] || entry.layerName);

  const displayTitle = isCourse ? courseInfo.capsuleSlug : entry.capsuleSlug;

  async function handleToggle() {
    if (!expanded) {
      setLoadingDetail(true);
      try {
        const res = await fetch(`/api/submissions/${entry.id}`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setDetail(data.submission);
        }
      } catch {
        // Failed to load detail - card will still expand with what we have
      } finally {
        setLoadingDetail(false);
      }
    }
    setExpanded(!expanded);
  }

  return (
    <div className="submission-card">
      <div className="submission-card-header" onClick={handleToggle}>
        <div className="submission-card-info">
          <div className="submission-card-title">
            {isCourse && <span style={{ fontSize: 11, color: 'var(--color-accent)', marginRight: 6 }}>Course</span>}
            {displayTitle}
          </div>
          <div className="submission-card-meta">
            <span className="submission-card-layer">{layerLabel}</span>
            <span className="submission-card-date">
              {formatDate(entry.createdAt)}
            </span>
          </div>
        </div>

        <span
          className={`submission-status ${
            entry.status === 'reviewed'
              ? 'submission-status-reviewed'
              : 'submission-status-pending'
          }`}
        >
          {entry.status === 'reviewed' ? 'Reviewed' : 'Pending'}
        </span>

        <span
          className={`submission-card-expand ${expanded ? 'expanded' : ''}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="4 6 8 10 12 6" />
          </svg>
        </span>
      </div>

      {expanded && (
        <div className="submission-card-detail">
          {loadingDetail ? (
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Loading...
            </p>
          ) : detail ? (
            <>
              <RecordingPlayer
                src={detail.recordingUrl}
                timestamps={detail.feedback?.timestamps}
              />

              {detail.note && (
                <div className="submission-note">
                  <div className="submission-note-label">Your Note</div>
                  {detail.note}
                </div>
              )}

              {detail.feedback && (
                <FeedbackView feedback={detail.feedback} />
              )}

              <div className="submission-card-actions">
                <Link
                  to={
                    isCourse
                      ? `/courses/${courseInfo.courseSlug}/${courseInfo.capsuleSlug}?record=true&layer=${encodeURIComponent(entry.layerName)}`
                      : `/capsule/${entry.capsuleSlug}?record=true&layer=${encodeURIComponent(entry.layerName)}`
                  }
                  className="btn btn-secondary btn-sm"
                >
                  Re-record and Resubmit
                </Link>
              </div>
            </>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Could not load submission details.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function FeedbackView({ feedback }: { feedback: Feedback }) {
  return (
    <div className="submission-feedback">
      <div className="submission-feedback-header">
        <span className="submission-feedback-title">Teacher Feedback</span>
        <span className="submission-feedback-reviewer">
          {feedback.reviewerEmail}
        </span>
      </div>
      <div className="submission-feedback-text">{feedback.text}</div>
      {feedback.videoUrl && (
        <div style={{ marginTop: 'var(--space-sm)' }}>
          <video
            src={feedback.videoUrl}
            controls
            playsInline
            style={{ width: '100%', maxWidth: 400, maxHeight: 300, borderRadius: 'var(--radius-md)', background: '#000' }}
          />
        </div>
      )}
      {feedback.timestamps && feedback.timestamps.length > 0 && (
        <div className="submission-feedback-timestamps">
          {feedback.timestamps.map((ts, i) => (
            <div key={i} className="submission-timestamp">
              <span className="submission-timestamp-time">
                {formatTime(ts.time)}
              </span>
              <span className="submission-timestamp-comment">
                {ts.comment}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SubmissionHistoryPage() {
  const { submissions, loading, error } = useSubmissions();

  return (
    <div className="submissions-page">
      <Header />
      <div className="submissions-content">
        <div className="submissions-header">
          <h1>My Submissions</h1>
          <p>Your practice recordings and teacher feedback</p>
        </div>

        {loading && (
          <div className="submissions-loading">Loading submissions...</div>
        )}

        {error && <div className="submissions-error">{error}</div>}

        {!loading && !error && submissions.length === 0 && (
          <div className="submissions-empty">
            <div className="submissions-empty-title">No submissions yet</div>
            <p className="submissions-empty-message">
              Record yourself practicing a capsule layer and submit it for
              feedback.
            </p>
            <Link to="/library" className="btn btn-primary">
              Browse Library
            </Link>
          </div>
        )}

        {!loading && !error && submissions.length > 0 && (
          <div className="submissions-list">
            {submissions.map((entry) => (
              <SubmissionCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
